import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { RefreshCw, Download, Calendar, Database, TrendingUp, Activity, Users } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { UsersModal } from './UsersModal';

export const CugsAnalyticsDashboard = () => {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedTables, setSelectedTables] = useState(new Set());
  const [viewMode, setViewMode] = useState('daily');
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');
  const [showUserDialog, setShowUserDialog] = useState(false);

  const availableTables = [
    'User', 'UserProfile', 'Thread', 'ThreadMessage', 'ThreadPoll',
    'Review', 'ReviewReply', 'ResearchArticle', 'ResearchDiscussion',
    'Dispensary', 'DispensaryProduct', 'UploadedDispensary', 'UploadedProduct',
    'UserGrowRoom', 'UserPlant', 'UserPot', 'UserInventory',
    'Hashtag', 'UserBadge', 'UserAchievement', 'ShopPurchase'
  ];

  const tableColors = {
    'User': '#ef4444', 'UserProfile': '#f97316', 'Thread': '#eab308',
    'ThreadMessage': '#84cc16', 'ThreadPoll': '#22c55e', 'Review': '#10b981',
    'ReviewReply': '#06b6d4', 'ResearchArticle': '#0ea5e9', 'ResearchDiscussion': '#3b82f6',
    'Dispensary': '#6366f1', 'DispensaryProduct': '#8b5cf6', 'UploadedDispensary': '#a855f7',
    'UploadedProduct': '#d946ef', 'UserGrowRoom': '#ec4899', 'UserPlant': '#f43f5e',
    'UserPot': '#fb7185', 'UserInventory': '#fdba74', 'Hashtag': '#fbbf24',
    'UserBadge': '#a3a3a3', 'UserAchievement': '#737373', 'ShopPurchase': '#525252'
  };

  useEffect(() => {
    loadAnalyticsData();
    setSelectedTables(new Set(availableTables));
  }, []);

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/table-counts`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to load analytics data');
      }
      
      const data = await response.json();
      setAnalyticsData(data.entries || []);
      setLastUpdate(data.lastUpdate);
    } catch (err) {
      setError(err.message);
      console.error('Analytics load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateTableCounts = async () => {
    setUpdating(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/analytics/update-counts`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to update table counts');
      }
      const result = await response.json();

      await loadAnalyticsData();
    } catch (err) {
      setError(err.message);
      console.error('Update error:', err);
    } finally {
      setUpdating(false);
    }
  };

  const handleForceUpdate = async () => {
    if (!window.confirm('Force ALL users to refresh? Continue?')) return;

    setIsLoadingUpdate(true);
    setUpdateMessage('');
    
    try {
      const response = await fetch(`${API_URL}/api/admin/force-update`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setUpdateMessage('✅ Success! Users will be prompted to update.');
      } else {
        setUpdateMessage('❌ Failed. Check permissions.');
      }
    } catch (error) {
      setUpdateMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsLoadingUpdate(false);
    }
  };

  const toggleTableSelection = (tableName) => {
    const newSelection = new Set(selectedTables);
    if (newSelection.has(tableName)) {
      newSelection.delete(tableName);
    } else {
      newSelection.add(tableName);
    }
    setSelectedTables(newSelection);
  };

  const selectAllTables = () => {
    setSelectedTables(new Set(availableTables));
  };

  const clearAllTables = () => {
    setSelectedTables(new Set());
  };

  const processedData = analyticsData.map(entry => {
    const processed = {
      timestamp: entry.timestamp,
      date: new Date(entry.timestamp).toLocaleDateString(),
      time: new Date(entry.timestamp).toLocaleTimeString(),
      fullDate: new Date(entry.timestamp).toLocaleString()
    };

    availableTables.forEach(table => {
      processed[table] = entry.counts[table] || 0;
    });

    return processed;
  });

  const getFilteredData = () => {
    if (viewMode === 'hourly') {
      return processedData.slice(-24);
    } else if (viewMode === 'daily') {
      return processedData.slice(-30);
    } else {
      return processedData;
    }
  };

  const filteredData = getFilteredData();

  const calculateSummaryStats = () => {
    if (filteredData.length === 0) return {};
    
    const latest = filteredData[filteredData.length - 1];
    const previous = filteredData.length > 1 ? filteredData[filteredData.length - 2] : {};
    
    const stats = {};
    selectedTables.forEach(table => {
      const current = latest[table] || 0;
      const prev = previous[table] || 0;
      const change = current - prev;
      
      stats[table] = {
        current,
        change,
        changePercent: prev > 0 ? ((change / prev) * 100).toFixed(1) : 0
      };
    });
    
    return stats;
  };

  const summaryStats = calculateSummaryStats();

  return (
    <div style={{
      background: '#0a0a0a',
      color: 'white',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <div>
            <h1 style={{
              margin: '0 0 8px 0',
              fontSize: '32px',
              fontWeight: '800',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              Cugs Analytics Dashboard
            </h1>
            <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
              Real-time database analytics and growth tracking
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowUserDialog(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <Users size={16} />
              View Users
            </button>
            <button
              onClick={updateTableCounts}
              disabled={updating}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: updating ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: updating ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <RefreshCw size={16} style={{ 
                animation: updating ? 'spin 1s linear infinite' : 'none' 
              }} />
              {updating ? 'Updating...' : 'Update Now'}
            </button>
          </div>
        </div>

        {/* Force Update Section */}
        <div style={{
          background: '#1a1a1a',
          border: '2px solid #ef4444',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#ef4444'
          }}>
            🔄 Force Client Update
          </h3>
          <p style={{ 
            margin: '0 0 16px 0', 
            color: '#888', 
            fontSize: '14px' 
          }}>
            Click after deploying new frontend code to force all users to refresh.
          </p>
          <button
            onClick={handleForceUpdate}
            disabled={isLoadingUpdate}
            style={{
              padding: '10px 20px',
              backgroundColor: isLoadingUpdate ? '#555' : '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: isLoadingUpdate ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            {isLoadingUpdate ? 'Triggering...' : '🔄 Force All Users to Update'}
          </button>
          {updateMessage && (
            <div style={{ 
              marginTop: '12px',
              padding: '12px',
              background: updateMessage.includes('✅') ? '#10b98120' : '#ef444420',
              border: `1px solid ${updateMessage.includes('✅') ? '#10b981' : '#ef4444'}`,
              borderRadius: '8px',
              color: updateMessage.includes('✅') ? '#10b981' : '#ef4444',
              fontSize: '14px'
            }}>
              {updateMessage}
            </div>
          )}
        </div>

        {/* Status Bar */}
        {lastUpdate && (
          <div style={{
            background: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <Activity size={20} style={{ color: '#10b981' }} />
            <span style={{ color: '#e5e5e5', fontSize: '14px' }}>
              Last updated: {new Date(lastUpdate).toLocaleString()}
            </span>
            <span style={{ color: '#888', fontSize: '14px' }}>
              • {analyticsData.length} total entries
            </span>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{
            background: '#ef444420',
            border: '1px solid #ef4444',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#ef4444',
            fontSize: '14px'
          }}>
            Error: {error}
          </div>
        )}

        {/* Controls */}
        <div style={{
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          
          {/* View Mode Toggle */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>
              Time Range
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[
                { key: 'hourly', label: 'Last 24 Hours' },
                { key: 'daily', label: 'Last 30 Days' },
                { key: 'all', label: 'All Time' }
              ].map(mode => (
                <button
                  key={mode.key}
                  onClick={() => setViewMode(mode.key)}
                  style={{
                    padding: '8px 16px',
                    background: viewMode === mode.key ? '#ef4444' : '#2a2a2a',
                    color: viewMode === mode.key ? 'white' : '#888',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          {/* Table Selection */}
          <div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '12px' 
            }}>
              <label style={{ color: '#e5e5e5', fontSize: '14px', fontWeight: '500' }}>
                Select Tables ({selectedTables.size}/{availableTables.length})
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={selectAllTables}
                  style={{
                    padding: '4px 8px',
                    background: '#2a2a2a',
                    color: '#10b981',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Select All
                </button>
                <button
                  onClick={clearAllTables}
                  style={{
                    padding: '4px 8px',
                    background: '#2a2a2a',
                    color: '#ef4444',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '8px'
            }}>
              {availableTables.map(table => (
                <label
                  key={table}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                    background: selectedTables.has(table) ? '#ef444420' : '#2a2a2a',
                    border: `1px solid ${selectedTables.has(table) ? '#ef4444' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedTables.has(table)}
                    onChange={() => toggleTableSelection(table)}
                    style={{ margin: 0 }}
                  />
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: tableColors[table],
                    borderRadius: '2px'
                  }} />
                  <span style={{ color: '#e5e5e5' }}>{table}</span>
                  {summaryStats[table] && (
                    <span style={{ 
                      color: '#888', 
                      fontSize: '11px',
                      marginLeft: 'auto' 
                    }}>
                      {summaryStats[table].current}
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {Object.keys(summaryStats).length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {Array.from(selectedTables).slice(0, 6).map(table => (
              <div
                key={table}
                style={{
                  background: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '12px',
                  padding: '16px'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    background: tableColors[table],
                    borderRadius: '2px'
                  }} />
                  <span style={{ color: '#888', fontSize: '12px' }}>{table}</span>
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#e5e5e5', marginBottom: '4px' }}>
                  {summaryStats[table]?.current || 0}
                </div>
                {summaryStats[table]?.change !== 0 && (
                  <div style={{
                    fontSize: '12px',
                    color: summaryStats[table]?.change > 0 ? '#10b981' : '#ef4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <TrendingUp size={12} />
                    {summaryStats[table]?.change > 0 ? '+' : ''}{summaryStats[table]?.change}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Chart */}
        <div style={{
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '24px'
        }}>
          <h3 style={{
            margin: '0 0 24px 0',
            fontSize: '18px',
            fontWeight: '600',
            color: '#e5e5e5',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Database size={20} />
            Table Growth Over Time
          </h3>

          {loading ? (
            <div style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#888'
            }}>
              Loading analytics data...
            </div>
          ) : filteredData.length === 0 ? (
            <div style={{
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: '#888'
            }}>
              <Database size={48} style={{ marginBottom: '16px' }} />
              <p>No analytics data available. Click "Update Now" to generate your first entry.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={filteredData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis 
                  dataKey={viewMode === 'hourly' ? 'time' : 'date'}
                  stroke="#888"
                  fontSize={12}
                />
                <YAxis stroke="#888" fontSize={12} />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #444',
                    borderRadius: '8px',
                    color: '#e5e5e5'
                  }}
                  labelStyle={{ color: '#888' }}
                />
                <Legend />
                
                {Array.from(selectedTables).map(table => (
                  <Line
                    key={table}
                    type="monotone"
                    dataKey={table}
                    stroke={tableColors[table]}
                    strokeWidth={2}
                    dot={{ fill: tableColors[table], strokeWidth: 0, r: 3 }}
                    activeDot={{ r: 5, stroke: tableColors[table], strokeWidth: 2, fill: '#1a1a1a' }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Users Modal */}
      <UsersModal 
        isOpen={showUserDialog} 
        onClose={() => setShowUserDialog(false)} 
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};